import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Download, Instagram } from "lucide-react";
import { useLang } from "@/context/LangContext";

const W = 1080;
const H = 1920;

const loadImg = (src) =>
  new Promise((res) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = () => res(null);
    img.src = src;
  });

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawCover = (ctx, img, x, y, w, h) => {
  const ir = img.width / img.height;
  const tr = w / h;
  let sw, sh, sx, sy;
  if (ir > tr) { sh = img.height; sw = sh * tr; sx = (img.width - sw) / 2; sy = 0; }
  else { sw = img.width; sh = sw / tr; sx = 0; sy = (img.height - sh) / 2; }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
};

const drawSmiley = (ctx, cx, cy, r) => {
  ctx.save();
  ctx.fillStyle = "#D4FF00";
  ctx.strokeStyle = "#09090B";
  ctx.lineWidth = 10;
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.fillStyle = "#09090B";
  ctx.beginPath(); ctx.arc(cx - r * 0.35, cy - r * 0.18, r * 0.11, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(cx + r * 0.35, cy - r * 0.18, r * 0.11, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = "#09090B"; ctx.lineWidth = r * 0.14; ctx.lineCap = "round";
  ctx.beginPath(); ctx.arc(cx, cy + r * 0.02, r * 0.5, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke();
  ctx.restore();
};

const KitStoryCard = ({ open, onClose, selected, count, discount, total }) => {
  const { t, tr } = useLang();
  const canvasRef = useRef(null);
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const draw = async () => {
      try { await document.fonts.load('900 120px "Saira Condensed"'); await document.fonts.ready; } catch (e) {}
      const imgs = await Promise.all(selected.slice(0, 4).map((s) => loadImg(s.product.image)));
      if (cancelled) return;
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");

      // Background gradient
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, "#FF3D8B"); g.addColorStop(0.45, "#C24BFF"); g.addColorStop(0.7, "#7B5CFF"); g.addColorStop(1, "#2FB8FF");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

      // deco blobs
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = "#D4FF00"; ctx.beginPath(); ctx.arc(150, 250, 170, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#00F0FF"; ctx.beginPath(); ctx.arc(W - 120, H - 380, 150, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;

      const cx = W / 2;
      ctx.textAlign = "center";

      // Top pill
      ctx.fillStyle = "#09090B"; roundRect(ctx, cx - 320, 110, 640, 78, 39); ctx.fill();
      ctx.fillStyle = "#D4FF00"; ctx.font = '900 40px "Saira Condensed"';
      ctx.fillText("★ FIGHT FOR YOURSELF ★", cx, 165);

      // Title
      ctx.fillStyle = "#F9F8F6"; ctx.font = '900 170px "Saira Condensed"';
      const [w1, ...rest] = t.builder.myKit.split(" ");
      ctx.fillText(w1, cx, 380);
      ctx.fillText(rest.join(" "), cx, 540);

      // Collage 2x2
      const tile = 380, gap = 36;
      const gw = tile * 2 + gap, gx = cx - gw / 2, gy = 640;
      const positions = [[gx, gy], [gx + tile + gap, gy], [gx, gy + tile + gap], [gx + tile + gap, gy + tile + gap]];
      const grads = ["#FF3366", "#D4FF00", "#B084FF", "#00F0FF"];
      imgs.forEach((img, i) => {
        const [x, y] = positions[i];
        ctx.save();
        roundRect(ctx, x, y, tile, tile, 40); ctx.fillStyle = grads[i % 4]; ctx.fill();
        ctx.save(); roundRect(ctx, x, y, tile, tile, 40); ctx.clip();
        if (img) { ctx.globalAlpha = 0.95; drawCover(ctx, img, x, y, tile, tile); ctx.globalAlpha = 1; }
        ctx.restore();
        ctx.lineWidth = 10; ctx.strokeStyle = "#09090B"; roundRect(ctx, x, y, tile, tile, 40); ctx.stroke();
        ctx.restore();
      });
      // Smiley stamp overlapping collage
      const extraCount = selected.length - 4;
      if (selected.length > 4) {
        const [x, y] = positions[3];
        ctx.fillStyle = "rgba(9,9,11,0.72)"; ctx.save(); roundRect(ctx, x, y, tile, tile, 40); ctx.clip(); ctx.fillRect(x, y, tile, tile); ctx.restore();
        ctx.fillStyle = "#D4FF00"; ctx.font = '900 120px "Saira Condensed"'; ctx.textAlign = "center";
        ctx.fillText(`+${extraCount + 1}`, x + tile / 2, y + tile / 2 + 42);
      }
      drawSmiley(ctx, gx + gw - 30, gy - 30, 92);

      // Stats panel
      const py = gy + tile * 2 + gap + 60, ph = 300, pw = 920, px = cx - pw / 2;
      ctx.fillStyle = "#F9F8F6"; roundRect(ctx, px, py, pw, ph, 44); ctx.fill();
      ctx.lineWidth = 10; ctx.strokeStyle = "#09090B"; roundRect(ctx, px, py, pw, ph, 44); ctx.stroke();
      const cols = [
        { big: String(count), small: t.builder.items },
        { big: `−${discount}%`, small: t.builder.discount },
        { big: `€${total.toFixed(0)}`, small: t.builder.total },
      ];
      const colW = pw / 3;
      cols.forEach((col, i) => {
        const ccx = px + colW * i + colW / 2;
        ctx.fillStyle = i === 1 ? "#FF3366" : "#09090B";
        ctx.font = '900 96px "Saira Condensed"';
        ctx.fillText(col.big, ccx, py + 150);
        ctx.fillStyle = "#9a9a9a"; ctx.font = '900 34px "Saira Condensed"';
        ctx.fillText(col.small.toUpperCase(), ccx, py + 210);
        if (i < 2) { ctx.strokeStyle = "#e2ddd4"; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(px + colW * (i + 1), py + 50); ctx.lineTo(px + colW * (i + 1), py + ph - 50); ctx.stroke(); }
      });

      // Footer
      ctx.fillStyle = "#F9F8F6"; ctx.font = '900 44px "Saira Condensed"';
      ctx.fillText("ANTIUNHEALTHYCLUB.COM", cx, py + ph + 130);
      ctx.fillStyle = "#D4FF00"; ctx.font = '900 38px "Saira Condensed"';
      ctx.fillText("#FIGHTFORYOURSELF", cx, py + ph + 185);

      try { setUrl(c.toDataURL("image/png")); } catch (e) { setUrl(null); }
    };

    draw();
    return () => { cancelled = true; };
  }, [open, selected, count, discount, total, t]);

  const download = () => {
    const href = url || (canvasRef.current && canvasRef.current.toDataURL("image/png"));
    if (!href) return;
    const a = document.createElement("a");
    a.href = href; a.download = "my-club-kit.png"; a.click();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 bg-ink/70 z-[80]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none"
            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.94 }}
          >
            <div className="pointer-events-auto border border-ink bg-cream w-full max-w-sm max-h-[92vh] overflow-y-auto p-5" data-testid="story-card-modal">
              <div className="flex items-center justify-between mb-3">
                <p className="display uppercase text-xl">{t.builder.storyBtn}</p>
                <button onClick={onClose} data-testid="story-close" className="btn bg-cream w-9 h-9 p-0 hover:bg-ink hover:text-cream">
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
              <div className="border border-ink overflow-hidden bg-sand">
                <canvas ref={canvasRef} width={W} height={H} className="w-full h-auto block" data-testid="story-canvas" />
              </div>
              <p className="text-xs text-center text-ink/60 mt-3 flex items-center justify-center gap-1.5">
                <Instagram className="w-3.5 h-3.5" strokeWidth={2.5} /> {t.builder.storyHint}
              </p>
              <button onClick={download} data-testid="story-download" className="btn-primary w-full h-13 py-3.5 mt-3">
                <Download className="w-5 h-5" strokeWidth={2.5} /> {t.builder.download}
              </button>
              <p className="text-xs text-center text-ink/50 mt-2">{t.builder.storyTag}</p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default KitStoryCard;
